/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {forwardRef, memo, useCallback} from 'react';
import {useSelector} from 'react-redux';
import {translate} from '@ion/core/src/locale/locale';
import {loadRoutes} from '@ion/core/src/services/Connect';
import GroupProperties from '@ion/core/src/services/GroupProperties';
import FilterEmptyState from '@ion/dls/src/filters/FilterEmptyState';
import {RouteIcon} from '@ion/dls/src/icons/RouteIcon';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {IconSize} from '@ion/dls/src/types/types';
import changePage, {Route} from '@/routes/RouteDefinitions';
import {setRouteFilters} from '@/store/actions/tracking';
import {globalGroupIdSelector} from '@/store/selectors/global-group-chooser-selectors';
import {trackingFiltersSelector} from '@/store/selectors/tracking-selectors';
import TrackingDrawerFilter, {
  TrackingDrawerFilterHandle,
  TrackingDrawerFilterOption
} from './TrackingDrawerFilter';
import {TrackingFilterNavigationRequest, TrackingFilterStateKey} from './types';
import {requestTrackingFilterNavigation} from './utils';

export type TrackingRouteFilterHandle = TrackingDrawerFilterHandle;

export interface TrackingRouteFilterProps {
  onRequestNavigate?: TrackingFilterNavigationRequest;
  onSelectedCountChange?: (count: number) => void;
  onSelectionChange?: () => void;
}

export const TrackingRouteFilter = forwardRef<TrackingRouteFilterHandle, TrackingRouteFilterProps>(({
  onRequestNavigate,
  onSelectedCountChange,
  onSelectionChange
}, ref) => {
  const globalGroupId = useSelector(globalGroupIdSelector);
  const trackingFilters = useSelector(trackingFiltersSelector);
  const selectedRoutes = trackingFilters?.routes?.selected ?? [];

  const loadOptions = useCallback(async(): Promise<TrackingDrawerFilterOption[]> => {
    if (!globalGroupId) {
      return [];
    }
    await GroupProperties.getInheritedProperties(globalGroupId);
    const {routes} = await loadRoutes({
      accountId : globalGroupId,
      pageId    : 1,
      pageSize  : 1000,
      sortOn    : ['+name']
    });
    return routes?.map(route => ({
      ...route,
      label   : route?.name,
      payload : {
        id   : Number(route?.id),
        name : route?.name
      },
      value : route?.id
    })) ?? [];
  }, [globalGroupId]);

  const toSelectedValue = useCallback(selected => selected?.id ?? selected, []);
  const handleManageRoutes = useCallback(function() {
    requestTrackingFilterNavigation(function() {
      changePage(Route.ADMIN_ROUTES);
    }, onRequestNavigate);
  }, [onRequestNavigate]);

  return (
    <TrackingDrawerFilter ref={ref}
      applyFilter={setRouteFilters}
      filterStateKey={TrackingFilterStateKey.Routes}
      emptyState={(
        <FilterEmptyState title={translate('filters:tracking.routes.empty.title')}
          description={translate('filters:tracking.routes.empty.description')}
          actionLabel={translate('filters:tracking.routes.empty.action')}
          icon={<RouteIcon color={dlsColor('grey1')} size={IconSize.medium} />}
          onAction={handleManageRoutes} />
      )}
      loadOptions={loadOptions}
      selectedItems={selectedRoutes}
      searchPlaceholder={translate('filters:tracking.routes.searchPlaceholder')}
      toSelectedValue={toSelectedValue}
      onSelectedCountChange={onSelectedCountChange}
      onSelectionChange={onSelectionChange} />
  );
});

export default memo(TrackingRouteFilter);
